import React, { useState, useEffect } from "react";
import { Calendar, Clock, Eye, Download } from "lucide-react";

function InfoCard({ icon, title, text, children }) {
  return (
    <div className="rounded-3xl px-5 py-6 bg-black/70 ring-1 ring-yellow-500/35 flex flex-col h-full justify-between">
      <div>
        <div className="mb-2 text-yellow-400">{icon}</div>
        <h3 className="text-2xl font-bold mb-1 text-white">{title}</h3>
        <p className="text-slate-300 mb-2 text-base">{text}</p>
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

function WeatherCard() {
  const [weather, setWeather] = useState({
    temp: "--",
    high: "--",
    low: "--",
    condition: "Loading...",
    location: "Casablanca, Morocco"
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      // Using Casablanca coordinates
      const lat = 33.5731;
      const lon = -7.5898;
      
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=auto`
      );
      
      const data = await response.json();
      
      const weatherCode = data.current.weather_code;
      const condition = getWeatherCondition(weatherCode);
      
      setWeather({
        temp: Math.round(data.current.temperature_2m),
        high: Math.round(data.daily.temperature_2m_max[0]),
        low: Math.round(data.daily.temperature_2m_min[0]),
        condition: condition,
        location: "Casablanca, Morocco"
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching weather:", error);
      setWeather({
        temp: 24,
        high: 32,
        low: 16,
        condition: "Partly Cloudy",
        location: "Casablanca, Morocco"
      });
      setLoading(false);
    }
  };

  const getWeatherCondition = (code) => {
    if (code === 0) return "Clear Sky";
    if (code <= 3) return "Partly Cloudy";
    if (code <= 49) return "Foggy";
    if (code <= 59) return "Drizzle";
    if (code <= 69) return "Rain";
    if (code <= 79) return "Snow";
    if (code <= 84) return "Showers";
    return "Thunderstorm";
  };

  return (
    <div className="relative w-full h-[184px] p-5 text-white flex flex-col justify-between rounded-2xl overflow-hidden">
      {/* Background SVG with Gold/Black gradient */}
      <svg 
        fill="none" 
        viewBox="0 0 342 175" 
        height="175" 
        width="342" 
        xmlns="http://www.w3.org/2000/svg" 
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="none"
      >
        <path 
          fill="url(#paint0_linear_gold_black)" 
          d="M0 66.4396C0 31.6455 0 14.2484 11.326 5.24044C22.6519 -3.76754 39.6026 0.147978 73.5041 7.97901L307.903 62.1238C324.259 65.9018 332.436 67.7909 337.218 73.8031C342 79.8154 342 88.2086 342 104.995V131C342 151.742 342 162.113 335.556 168.556C329.113 175 318.742 175 298 175H44C23.2582 175 12.8873 175 6.44365 168.556C0 162.113 0 151.742 0 131V66.4396Z"
        />
        <defs>
          <linearGradient 
            gradientUnits="userSpaceOnUse" 
            y2="128" 
            x2="354.142" 
            y1="128" 
            x1="0" 
            id="paint0_linear_gold_black"
          >
            <stop stopColor="#D4AF37" />
            <stop stopColor="#1a1a1a" offset="1" />
          </linearGradient>
        </defs>
      </svg>

      {/* Cloud SVG */}
      <div className="absolute -top-3 right-0 z-10">
        <svg 
          fill="#000000" 
          preserveAspectRatio="xMidYMid meet" 
          className="h-[120px]" 
          role="img" 
          aria-hidden="true" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 64 64"
        >
          <g fill="#FFD700">
            <path d="M10.8 42.9c-.5 1.5-.1 3 1 3.4c1.1.4 2.4-.5 3-2c.6-1.8.7-4.1.2-6.9c-2.1 1.9-3.6 3.8-4.2 5.5" />
            <path d="M13.2 57.4c.6-1.8.7-4.1.2-6.9c-2.1 1.8-3.6 3.7-4.2 5.5c-.5 1.5-.1 3 1 3.4c1.1.4 2.5-.5 3-2" />
            <path d="M51.5 37.4c-2.1 1.8-3.6 3.7-4.2 5.5c-.5 1.5-.1 3 1 3.4c1.1.4 2.4-.5 3-2c.5-1.7.6-4.1.2-6.9" />
            <path d="M38.2 55.9c-.5 1.5-.1 3 1 3.4s2.4-.5 3-2c.6-1.8.7-4.1.2-6.9c-2 1.9-3.5 3.8-4.2 5.5" />
            <path d="M46.9 55.9c-.5 1.5-.1 3 1 3.4s2.4-.5 3-2c.6-1.8.7-4.1.2-6.9c-2.1 1.9-3.6 3.8-4.2 5.5" />
            <path d="M18.6 55.9c-.5 1.5-.1 3 1 3.4s2.4-.5 3-2c.6-1.8.7-4.1.2-6.9c-2.1 1.9-3.6 3.8-4.2 5.5" />
          </g>
          <path d="M24.5 31.9l-4.9 16.2h12.5L27.9 62l16.5-20.2H32.5l2.9-9.9z" fill="#FFA500" />
          <path 
            fill="#ffffff" 
            d="M18.2 32.5c-.8 0-1.6-.1-2.4-.4c-3.1-1-5.3-3.9-5.3-7.2c0-2.2 1-4.3 2.6-5.7c.4-.4.9-.7 1.4-1l.5-1.8c1.3-4.4 5.4-7.5 10-7.5c.5 0 .9 0 1.5.1c.4.1.8.1 1.2.3l.2-.4c1.9-3.3 5.4-5.4 9.2-5.4C43 3.5 47.7 8.2 47.7 14v1c.4.2.9.4 1.3.6c2.8 1.6 4.5 4.6 4.5 7.8c0 4.2-2.9 7.8-7 8.8c-.7.2-1.4.2-2 .2H18.2z"
          />
          <path 
            fill="#FFE4B5" 
            d="M37.1 5c5 0 9 4 9 8.9v.7c-2.1.2-4 1-5.4 2.3c1.1-.6 2.4-1 3.7-1c.5 0 1 .1 1.5.1c.8.2 1.6.5 2.3.9c2.3 1.3 3.8 3.7 3.8 6.5c0 3.6-2.5 6.5-5.8 7.3c-.7.2-1.2.3-1.8.3H18.2c-.7 0-1.3-.1-1.9-.3c-2.4-.8-4.2-3.1-4.2-5.8c0-1.8.8-3.5 2.1-4.6c.6-.5 1.3-.9 2-1.2c.6-.2 1.3-.3 2-.3c2 0 3.7.9 4.9 2.4h.1c-1.3-2.4-3.7-4.1-6.6-4.3c1.1-3.7 4.5-6.4 8.5-6.4c.4 0 .9 0 1.3.1c.8.1 1.6.3 2.3.7c2.7 1.2 4.7 3.7 5.1 6.8V18c0-3.4-1.8-6.5-4.5-8.3C30.8 6.9 33.8 5 37.1 5m0-3C33 2 29.2 4.1 27 7.6h-.3c-.6-.1-1.2-.1-1.7-.1c-5.3 0-10 3.5-11.4 8.6l-.3 1.2c-.4.2-.7.5-1.1.8c-2 1.7-3.1 4.2-3.1 6.9c0 4 2.5 7.4 6.3 8.7c.9.3 1.9.5 2.9.5h26.2c.8 0 1.6-.1 2.4-.3c4.8-1.1 8.2-5.3 8.2-10.3c0-3.8-2-7.3-5.3-9.1c-.2-.1-.3-.2-.5-.3v-.1C49.2 7.4 43.8 2 37.1 2z"
          />
        </svg>
      </div>

      {/* Main Temperature */}
      <p className="text-5xl font-light z-20 relative">
        {loading ? "..." : `${weather.temp}°`}
      </p>

      {/* Info Section */}
      <div className="flex justify-between items-end z-20 relative">
        <div>
          <p className="text-white/60 text-sm">H:{weather.high}° L: {weather.low}°</p>
          <p className="text-sm">{weather.location}</p>
        </div>
        <p className="text-sm">{weather.condition}</p>
      </div>
    </div>
  );
}

export default function EventDetailsSection() {
  return (
    <section aria-labelledby="details" className="py-16 px-4 bg-gradient-to-b from-black to-slate-900">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-10">
          <InfoCard 
            icon={<Calendar size={32} />} 
            title="Event Dates" 
            text="Friday 16th - Saturday 17th January 2026" 
          />
          
          <InfoCard 
            icon={<Clock size={32} />} 
            title="Schedule" 
            text="Friday 16th 13:30–18:30 | Saturday 17th 09:30–12:00"
          >
            <div className="flex flex-row gap-2">
              <a 
                href="/pdfs/AMBF26_program.pdf" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-3 rounded-xl transition-colors duration-200 text-sm"
              >
                <Eye size={16} />
              </a>
              <a 
                href="/pdfs/program.pdf" 
                download="event-program.pdf"
                className="flex items-center justify-center gap-2 bg-black hover:bg-slate-900 text-yellow-400 font-semibold py-2 px-3 rounded-xl ring-1 ring-yellow-500/50 hover:ring-yellow-500 transition-all duration-200 text-sm"
              >
                <Download size={16} />
              </a>
            </div>
          </InfoCard>
          
          <WeatherCard />
        </div>
      </div>
    </section>
  );
}